import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

const toasts = ref([])

function pushToast({ title, description, variant = 'default', duration = 3000 }) {
  const id = Date.now() + Math.random()
  toasts.value.push({ id, title, description, variant })
  setTimeout(() => {
    toasts.value = toasts.value.filter(t => t.id !== id)
  }, duration)
  return {
    id,
    dismiss: () => { toasts.value = toasts.value.filter(t => t.id !== id) },
    update: (props) => {
      const idx = toasts.value.findIndex(t => t.id === id)
      if (idx !== -1) toasts.value[idx] = { ...toasts.value[idx], ...props }
    },
  }
}

// Translate a string ONLY if it looks like an i18n key (contains a dot, no spaces).
// Otherwise pass through as a literal — keeps migration safe for existing call sites.
// Stricter than "contains a dot" — requires a key-like shape:
// starts with a letter, lowercase alphanumeric segments separated by dots.
// Excludes emails (@), URLs (/, :), version strings (digits-leading), file names, etc.
const KEY_RE = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/i

function looksLikeKey(s) {
  return typeof s === 'string' && KEY_RE.test(s)
}

export function useToast() {
  let t = (k) => k
  try {
    const i18n = useI18n()
    t = i18n.t
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[useToast] vue-i18n unavailable — using identity translator. Original error:', err?.message ?? err)
    }
  }

  const translate = (input, params) =>
    looksLikeKey(input) ? t(input, params || {}) : input

  return {
    // Legacy passthrough — existing call sites keep working unchanged.
    toast: pushToast,
    toasts,

    // New typed shortcuts.
    success: (description, { titleKey, params, duration } = {}) => pushToast({
      title: translate(titleKey ?? 'common.toasts.success', params),
      description: translate(description, params),
      variant: 'success',
      ...(duration ? { duration } : {}),
    }),
    error: (description, { titleKey, params, duration } = {}) => pushToast({
      title: translate(titleKey ?? 'common.toasts.error', params),
      description: translate(description, params),
      variant: 'destructive',
      ...(duration ? { duration } : {}),
    }),
    info: (description, { titleKey, params, duration } = {}) => pushToast({
      title: translate(titleKey ?? 'common.toasts.info', params),
      description: translate(description, params),
      variant: 'default',
      ...(duration ? { duration } : {}),
    }),
  }
}
